import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { faqAPI } from '../../services/api';
import { FAQ } from '../../types';
import './FAQManagement.css';

const FAQManagement: React.FC = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentFAQ, setCurrentFAQ] = useState<FAQ | null>(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: '',
    display_order: 0,
    is_active: true,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchFAQs();
    fetchCategories();
  }, []);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const response = await faqAPI.getAll();
      setFaqs(response.data.faqs);
    } catch (error) {
      console.error('SSS yükleme hatası:', error);
      setError('Sorular yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await faqAPI.getCategories();
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Kategori yükleme hatası:', error);
    }
  };

  const handleOpenModal = (faq?: FAQ) => {
    if (faq) {
      setEditMode(true);
      setCurrentFAQ(faq);
      setFormData({
        question: faq.question,
        answer: faq.answer,
        category: faq.category || '',
        display_order: faq.display_order,
        is_active: faq.is_active,
      });
    } else {
      setEditMode(false);
      setCurrentFAQ(null);
      setFormData({
        question: '',
        answer: '',
        category: '',
        display_order: faqs.length + 1,
        is_active: true,
      });
    }
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      question: '',
      answer: '',
      category: '',
      display_order: 0,
      is_active: true,
    });
    setEditMode(false);
    setCurrentFAQ(null);
    setError('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.question.trim()) {
      setError('Soru gereklidir');
      return;
    }

    if (!formData.answer.trim()) {
      setError('Cevap gereklidir');
      return;
    }

    try {
      const submitData = {
        question: formData.question,
        answer: formData.answer,
        category: formData.category || undefined,
        display_order: formData.display_order,
        is_active: formData.is_active,
      };

      if (editMode && currentFAQ) {
        await faqAPI.update(currentFAQ.id, submitData);
        setSuccess('Soru başarıyla güncellendi');
      } else {
        await faqAPI.create(submitData);
        setSuccess('Soru başarıyla eklendi');
      }

      setTimeout(() => {
        handleCloseModal();
        fetchFAQs();
        fetchCategories();
      }, 1500);
    } catch (error: any) {
      setError(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Bu soruyu silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await faqAPI.delete(id);
      setSuccess('Soru başarıyla silindi');
      fetchFAQs();
      fetchCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Silme işlemi başarısız');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleToggleActive = async (faq: FAQ) => {
    try {
      await faqAPI.update(faq.id, {
        is_active: !faq.is_active,
      });
      fetchFAQs();
    } catch (error) {
      console.error('Durum değiştirme hatası:', error);
    }
  };

  // Kategorilere göre grupla
  const groupedFAQs = faqs.reduce((acc, faq) => {
    const cat = faq.category || 'Kategori Yok';
    if (!acc[cat]) {
      acc[cat] = [];
    }
    acc[cat].push(faq);
    return acc;
  }, {} as Record<string, FAQ[]>);

  return (
    <div className="admin-dashboard">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <h2>
            <i className="bi bi-shield-lock-fill me-2"></i>
            Admin Panel
          </h2>
        </div>

        <nav className="sidebar-nav">
          <Link to="/admin" className="nav-item">
            <i className="bi bi-speedometer2"></i>
            Dashboard
          </Link>
          <Link to="/admin/announcements" className="nav-item">
            <i className="bi bi-megaphone"></i>
            Duyurular
          </Link>
          <Link to="/admin/gallery" className="nav-item">
            <i className="bi bi-images"></i>
            Galeri
          </Link>
          <Link to="/admin/faqs" className="nav-item active">
            <i className="bi bi-question-circle"></i>
            SSS
          </Link>
          <Link to="/admin/users" className="nav-item">
            <i className="bi bi-people"></i>
            Kullanıcılar
          </Link>
        </nav>

        <div className="sidebar-footer">
          <Link to="/" className="btn btn-secondary w-100 mb-2">
            <i className="bi bi-house-door me-2"></i>
            Ana Sayfa
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-header">
          <div>
            <h1>SSS Yönetimi</h1>
            <p className="text-muted">Sıkça sorulan soruları yönetin</p>
          </div>
          <button onClick={() => handleOpenModal()} className="btn btn-primary">
            <i className="bi bi-plus-circle me-2"></i>
            Yeni Soru Ekle
          </button>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="alert alert-success">
            <i className="bi bi-check-circle-fill me-2"></i>
            {success}
          </div>
        )}

        {error && !showModal && (
          <div className="alert alert-danger">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {error}
          </div>
        )}

        {/* FAQ List */}
        <div className="faq-admin-container">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
            </div>
          ) : faqs.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-question-circle" style={{ fontSize: '3rem' }}></i>
              <p className="mt-3">Henüz soru yok</p>
            </div>
          ) : (
            <div className="faq-groups">
              {Object.entries(groupedFAQs)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([category, categoryFAQs]) => (
                  <div key={category} className="faq-category-section">
                    <h2 className="faq-category-header">
                      <i className="bi bi-folder2-open"></i>
                      {category}
                      <span className="badge bg-secondary ms-2">{categoryFAQs.length}</span>
                    </h2>
                    <div className="faq-table">
                      <table className="table">
                        <thead>
                          <tr>
                            <th style={{ width: '5%' }}>#</th>
                            <th style={{ width: '30%' }}>Soru</th>
                            <th style={{ width: '40%' }}>Cevap</th>
                            <th style={{ width: '10%' }}>Durum</th>
                            <th style={{ width: '15%' }}>İşlemler</th>
                          </tr>
                        </thead>
                        <tbody>
                          {categoryFAQs
                            .sort((a, b) => a.display_order - b.display_order)
                            .map((faq) => (
                              <tr key={faq.id}>
                                <td>{faq.display_order}</td>
                                <td>
                                  <div className="faq-question-cell">
                                    <i className="bi bi-question-circle-fill text-primary me-2"></i>
                                    {faq.question}
                                  </div>
                                </td>
                                <td>
                                  <div className="faq-answer-cell">
                                    {faq.answer.length > 100
                                      ? `${faq.answer.substring(0, 100)}...`
                                      : faq.answer}
                                  </div>
                                </td>
                                <td>
                                  <button
                                    onClick={() => handleToggleActive(faq)}
                                    className={`badge ${
                                      faq.is_active ? 'badge-success' : 'badge-secondary'
                                    }`}
                                  >
                                    {faq.is_active ? 'Aktif' : 'Pasif'}
                                  </button>
                                </td>
                                <td>
                                  <div className="action-buttons">
                                    <button
                                      onClick={() => handleOpenModal(faq)}
                                      className="btn btn-sm btn-primary"
                                      title="Düzenle"
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </button>
                                    <button
                                      onClick={() => handleDelete(faq.id)}
                                      className="btn btn-sm btn-danger"
                                      title="Sil"
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Soruyu Düzenle' : 'Yeni Soru Ekle'}</h3>
              <button onClick={handleCloseModal} className="modal-close" title="Kapat" aria-label="Kapat">
                <i className="bi bi-x-lg" aria-hidden="true"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-body">
              {error && (
                <div className="alert alert-danger">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {error}
                </div>
              )}

              {success && (
                <div className="alert alert-success">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  {success}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="question">Soru *</label>
                <input
                  type="text"
                  id="question"
                  name="question"
                  className="form-control"
                  value={formData.question}
                  onChange={handleChange}
                  required
                  placeholder="Soru metni"
                />
              </div>

              <div className="form-group">
                <label htmlFor="answer">Cevap *</label>
                <textarea
                  id="answer"
                  name="answer"
                  className="form-control"
                  rows={6}
                  value={formData.answer}
                  onChange={handleChange}
                  required
                  placeholder="Cevap metni"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="category">Kategori</label>
                  <input
                    type="text"
                    id="category"
                    name="category"
                    className="form-control"
                    value={formData.category}
                    onChange={handleChange}
                    placeholder="Örn: Genel, Hukuki, Prosedür"
                    list="categories-list"
                  />
                  <datalist id="categories-list">
                    {categories.map((cat) => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                </div>

                <div className="form-group">
                  <label htmlFor="display_order">Sıra</label>
                  <input
                    type="number"
                    id="display_order"
                    name="display_order"
                    className="form-control"
                    value={formData.display_order}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-check">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  className="form-check-input"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
                <label htmlFor="is_active" className="form-check-label">
                  Aktif
                </label>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={handleCloseModal} className="btn btn-secondary">
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  <i className="bi bi-save me-2"></i>
                  {editMode ? 'Güncelle' : 'Ekle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQManagement;
